import React, { useEffect } from "react";
import { connect } from "react-redux";
import { message, Form } from "antd";
import Layout from "components/layout";
import { RegisterButton } from "components/registerButton";
import { bindActionCreators } from "redux";
import { Creators as TagsActions } from "store/readers-tags/tags/tags-duck";
import { IItems, IResultCsvData, ITagForm } from "store/readers-tags/tags/tags-model";
import { Creators as UserSessionActions } from "store/user-session/user-session-duck";
import { IUserData, MenuItemActive } from "store/user-session/user-session-model";
import { Roles } from "utils/functions";
import useTranslation from "utils/hooks/useTranslation";
import TagDrawer from "./components/drawer";
import FormTags from "./components/form";
import ImportTag from "./components/modal";
import TagsFilter from "./filter";
import TagsGrid from "./grid";
import * as S from "./index.styled";
import { onConfirmDrawerUpdateTag } from "./tags.service";

interface IPropsScreenTags {
  editTag?: ITagForm;
  isLoading?: boolean;
  isLoadingTable?: boolean;
  userData?: IUserData;
  items?: Array<IItems>;
  totalItems?: number;
  pageIndex?: number;
  searchFilter?: string;
  visibleDrawer?: boolean;
  isTagActive?: boolean;
  isLoadingImportCsvModal?: boolean;
  visibleImportCsvModal?: boolean;
  resultCsvData?: IResultCsvData;
  tagWasInactive?: boolean;
  tagHasInactiveUserAccepted?: boolean;
  vehicleAlreadyHasTag?: boolean;
  vehicleAlreadyHasTagUserAccepted?: boolean;
  isLoadingEditDrawer?: boolean;
  tagNumber?: string;
  vehiclePlate?: string;
}

interface IDispatchProps {
  userSessionMenuItemActive?: (...args) => void;
  tagsRegisterFetchAsync?: (...args) => void;
  tagsChangeFieldValue?: (...args) => void;
  tagsUpdateFilter?: (...args) => void;
  tagsHideDrawer?: (...args) => void;
  tagsEventTagActive?: (...args) => void;
  tagsFetchByIdAsync?: (...args) => void;
  tagsHideImportCsvModal?: (...args) => void;
  tagsShowImportCsvModal?: (...args) => void;
  tagsUpdateImportCsvData?: (...args) => void;
  tagsImportCsvAsync?: (...args) => void;
  tagsDataTag?: (...args) => void;
  tagsUpdateTagAsync?: (...args) => void;
  tagsUpdateVehicleAlreadyHasTagUserAccepted?: (...args) => void;
  tagsUpdateTagHasInactiveUserAccepted?: (...args) => void;
  tagsUpdateResultCsvData?: (...args) => void;
}

export type IProps = IPropsScreenTags & IDispatchProps;

const Tags = ({
  items,
  editTag,
  userData,
  pageIndex,
  tagNumber,
  isLoading,
  totalItems,
  isTagActive,
  searchFilter,
  visibleDrawer,
  vehiclePlate,
  resultCsvData,
  isLoadingTable,
  tagWasInactive,
  isLoadingEditDrawer,
  vehicleAlreadyHasTag,
  visibleImportCsvModal,
  isLoadingImportCsvModal,
  tagHasInactiveUserAccepted,
  vehicleAlreadyHasTagUserAccepted,
  tagsDataTag,
  tagsHideDrawer,
  tagsUpdateFilter,
  tagsUpdateTagAsync,
  tagsFetchByIdAsync,
  tagsImportCsvAsync,
  tagsEventTagActive,
  tagsChangeFieldValue,
  tagsShowImportCsvModal,
  tagsHideImportCsvModal,
  tagsRegisterFetchAsync,
  tagsUpdateImportCsvData,
  tagsUpdateResultCsvData,
  userSessionMenuItemActive,
  tagsUpdateTagHasInactiveUserAccepted,
  tagsUpdateVehicleAlreadyHasTagUserAccepted,
}: IProps) => {
  const TITLE_PAGE = useTranslation({ id: "tags" });
  const ERROR_VALIDATING_FORM = useTranslation({ id: "error_validating_form" });
  const isAdmin = userData.role === Roles.ADMIN;
  const [form] = Form.useForm();

  useEffect(() => {
    userSessionMenuItemActive(MenuItemActive.TAGS);
  }, [userSessionMenuItemActive]);

  useEffect(() => {
    if (!visibleDrawer) {
      form.resetFields();
      tagsDataTag(null);
      tagsEventTagActive(false);
    }
  }, [visibleDrawer, form]);

  useEffect(() => {
    return () => {
      tagsUpdateFilter(null);
    };
  }, []);

  const handleOnConfirmDrawerEditTag = () => {
    onConfirmDrawerUpdateTag({
      formEdit: form,
      tagIdSelected: editTag.id,
      isTagActive,
      tagsUpdateTagAsync,
      messageDescription: ERROR_VALIDATING_FORM,
    });
  };

  const handleOnConfirmVehicleAlreadyHasTag = () => {
    tagsUpdateVehicleAlreadyHasTagUserAccepted(true);

    form
      .validateFields()
      .then((values) => {
        const bodyRequest = {
          id: editTag.id,
          vehiclePlate: values.vehiclePlate,
          status: isTagActive,
        };
        tagsUpdateTagAsync(bodyRequest);
      })
      .catch((error) => {
        console.error("Tag form validation error", error);
        message.error(ERROR_VALIDATING_FORM);
      });
  };

  const handleOnConfirmTagHasInactiveUserAccepted = () => {
    tagsUpdateTagHasInactiveUserAccepted(true);

    form
      .validateFields()
      .then((values) => {
        const bodyRequest = {
          id: editTag.id,
          vehiclePlate: values.vehiclePlate,
          status: isTagActive,
        };
        tagsUpdateTagAsync(bodyRequest);
      })
      .catch((error) => {
        console.error("Tag form validation error", error);
        message.error(ERROR_VALIDATING_FORM);
      });
  };

  return (
    <Layout title={TITLE_PAGE} isLoading={isLoading} role={userData.role}>
      <S.DefaultContainer>
        <S.HeaderContainer isAdmin={isAdmin}>
          {isAdmin && <RegisterButton id="import-csv" title="import_csv" dataTestId="importCsv" onClick={tagsShowImportCsvModal} />}
          <TagsFilter tagsUpdateFilter={tagsUpdateFilter} tagsChangeFieldValue={tagsChangeFieldValue} />
        </S.HeaderContainer>
        <TagDrawer
          width="680"
          confirmbuttontxt={useTranslation({ id: "save" })}
          cancelbuttontxt={useTranslation({ id: "cancel" })}
          maskClosable={true}
          disabled={isLoadingEditDrawer}
          onConfirm={handleOnConfirmDrawerEditTag}
          title={useTranslation({ id: "edit_tag" })}
          placement="right"
          closable={false}
          destroyOnClose={true}
          onClose={tagsHideDrawer}
          open={visibleDrawer}
          showConfirmButtonVehicleAlreadyHasTag={vehicleAlreadyHasTag && !vehicleAlreadyHasTagUserAccepted}
          showConfirmButtonTagWasInactivate={tagWasInactive && !tagHasInactiveUserAccepted}
          onConfirmVehicleAlreadyHasTag={handleOnConfirmVehicleAlreadyHasTag}
          onConfirmTagHasInactiveUserAccepted={handleOnConfirmTagHasInactiveUserAccepted}
          vehiclePlate={vehiclePlate}
          tagNumber={tagNumber}
        >
          <FormTags editTag={editTag} form={form} isTagActive={isTagActive} tagsEventTagActive={tagsEventTagActive} />
        </TagDrawer>
        <ImportTag
          isLoadingImportCsvModal={isLoadingImportCsvModal}
          tagsHideImportCsvModal={tagsHideImportCsvModal}
          visibleImportCsvModal={visibleImportCsvModal}
          tagsUpdateImportCsvData={tagsUpdateImportCsvData}
          tagsImportCsvAsync={tagsImportCsvAsync}
          resultCsvData={resultCsvData}
          tagsUpdateResultCsvData={tagsUpdateResultCsvData}
        />
        <TagsGrid
          tagsRegisterFetchAsync={tagsRegisterFetchAsync}
          tagsChangeFieldValue={tagsChangeFieldValue}
          tagsFetchByIdAsync={tagsFetchByIdAsync}
          items={items}
          totalItems={totalItems}
          pageIndex={pageIndex}
          searchFilter={searchFilter}
          isLoadingTable={isLoadingTable}
        />
      </S.DefaultContainer>
    </Layout>
  );
};
const mapDispatchToActions = (dispatch: any) => bindActionCreators({ ...UserSessionActions, ...TagsActions }, dispatch);

const mapStateToProps = (state) => ({
  isLoading: state.Tags.isLoading,
  isLoadingTable: state.Tags.isLoadingTable,
  userData: state.UserSession.userData,
  items: state.Tags.items,
  totalItems: state.Tags.totalItems,
  pageIndex: state.Tags.filter.pageIndex,
  searchFilter: state.Tags.searchFilter,
  visibleDrawer: state.Tags.visibleDrawer,
  isTagActive: state.Tags.editTag.isTagActive,
  editTag: state.Tags.editTag,
  visibleImportCsvModal: state.Tags.visibleImportCsvModal,
  isLoadingImportCsvModal: state.Tags.isLoadingImportCsvModal,
  resultCsvData: state.Tags.resultCsvData,
  tagWasInactive: state.Tags.tagWasInactive,
  tagHasInactiveUserAccepted: state.Tags.tagHasInactiveUserAccepted,
  vehicleAlreadyHasTag: state.Tags.vehicleTagInfo.vehicleAlreadyHasTag,
  tagNumber: state.Tags.vehicleTagInfo.tagNumber,
  vehiclePlate: state.Tags.vehicleTagInfo.vehiclePlate,
  vehicleAlreadyHasTagUserAccepted: state.Tags.vehicleAlreadyHasTagUserAccepted,
  isLoadingEditDrawer: state.Tags.isLoadingEditDrawer,
});

export default connect(mapStateToProps, mapDispatchToActions)(Tags);
